// client/src/components/PortfolioDropdown.tsx
"use client";

import React, { useState, useEffect, useRef } from 'react';
import { usePortfolio } from '@/contexts/PortfolioContext';
import { useRouter } from 'next/navigation'; // Use useRouter from next/navigation
import Link from 'next/link'; // Use next/link
import { apiRequest } from '@/utils/api';
import {
  ChevronDown,
  Plus,
  Share2,
  Trash2,
  Pencil,
  BarChart3,
  Settings,
  Eye,
  SquarePen
} from 'lucide-react';
import { trackAddPortfolio } from '@/analytics'; // Adjust path if necessary
import LoadingSpinner from './LoadingSpinner';
import { SUBSCRIPTION_TIERS } from '@/lib/subscription';
import { subscriptionAPI } from '@/lib/subscription';
import { Portfolio } from '@/types';

interface PortfolioDropdownProps {
  mobile?: boolean;
  onMobileMenuClose?: () => void;
}

const DEFAULT_PORTFOLIO_DELETE_TOOLTIP = "Your main portfolio can't be deleted";
const PORTFOLIO_DELETE_FAILED = "We couldn't delete this portfolio. Check your connection and try again.";

export default function PortfolioDropdown({ mobile = false, onMobileMenuClose }: PortfolioDropdownProps) {
  const router = useRouter();
  const {
    portfolios,
    activePortfolio,
    loading,
    dropdownOpen,
    setDropdownOpen,
    switchPortfolio,
    createPortfolio,
    deletePortfolio,
    updatePortfolio,
    sharePortfolio,
    removeShare,
    loadPortfolios: _loadPortfolios // Added to refresh after share actions
  } = usePortfolio();

  // Local state for portfolio creation loading
  const [isCreatingPortfolio, setIsCreatingPortfolio] = useState(false);
  const [userTier, setUserTier] = useState(SUBSCRIPTION_TIERS.FREE);
  const [defaultPortfolioId, setDefaultPortfolioId] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const ownedPortfolioCount = portfolios.filter((p: Portfolio) => p.isOwner).length;
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioDescription, setNewPortfolioDescription] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [showShareForm, setShowShareForm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user's subscription tier
  useEffect(() => {
    const fetchUserTier = async () => {
      try {
        const subscriptionStatus = await subscriptionAPI.getSubscriptionStatus();
        setUserTier(subscriptionStatus.tier || SUBSCRIPTION_TIERS.FREE);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        setUserTier(SUBSCRIPTION_TIERS.FREE);
      }
    };

    fetchUserTier();
  }, []);

  // Close forms when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCreateForm(false);
        setShowEditForm(false);
        setShowShareForm(false);
        setDropdownOpen(false);
      }
    };

    if (showCreateForm || showEditForm || showShareForm || dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateForm, showEditForm, showShareForm, dropdownOpen, setDropdownOpen]);

  // Fetch default portfolio ID
  useEffect(() => {
    const fetchDefaultPortfolioId = async () => {
      try {
        const data = await apiRequest<{ default_portfolio?: { id?: string } }>(
          'GET',
          '/user/default-portfolio'
        );
        if (data?.default_portfolio?.id) {
          setDefaultPortfolioId(data.default_portfolio.id);
        }
      } catch (err) {
        console.error('Error fetching default portfolio:', err);
      }
    };
    fetchDefaultPortfolioId();
  }, []);

  const handleCreatePortfolio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPortfolioName.trim()) return;

    setIsCreatingPortfolio(true);

    try {
      const createdPortfolio = await createPortfolio({
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || null
      });

      trackAddPortfolio(newPortfolioName.trim());

      setNewPortfolioName('');
      setNewPortfolioDescription('');
      setShowCreateForm(false);
      switchPortfolio(createdPortfolio); // Automatically switch to the new portfolio
    } catch (error) {
      console.error("Error creating portfolio:", error);
    } finally {
      setIsCreatingPortfolio(false);
    }
  };

  const handleUpdatePortfolio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPortfolioName.trim() || !editingPortfolio) return;

    try {
      await updatePortfolio(editingPortfolio.id, {
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || null
      });
      setNewPortfolioName('');
      setNewPortfolioDescription('');
      setShowEditForm(false);
      setEditingPortfolio(null);
      router.refresh(); // Refresh the current route
    } catch (error) {
      console.error("Error updating portfolio:", error);
    }
  };

  const handleDeletePortfolio = async (portfolioId: string) => {
    if (window.confirm('Are you sure you want to delete this portfolio? This action cannot be undone.')) {
      try {
        await deletePortfolio(portfolioId);
        router.refresh(); // Refresh the current route
      } catch (error) {
        console.error("Error deleting portfolio:", error);
        const serverMessage = (error as { response?: { data?: { error?: unknown } } })
          ?.response?.data?.error;
        window.alert(
          typeof serverMessage === 'string' && serverMessage
            ? serverMessage
            : PORTFOLIO_DELETE_FAILED
        );
      }
    }
  };

  const handleSharePortfolio = async (portfolioId: string) => {
    if (!shareEmail.trim()) return;

    try {
      await sharePortfolio(portfolioId, shareEmail.trim(), sharePermission);
      setShareEmail('');
      setSharePermission('view');
      setShowShareForm(false);
      router.refresh(); // Refresh to show updated shared users
    } catch (error) {
      console.error("Error sharing portfolio:", error);
    }
  };

  const _handleRemoveShare = async (portfolioId: string, userId: string) => {
    try {
      await removeShare(portfolioId, userId);
      router.refresh(); // Refresh to show updated shared users
    } catch (error) {
      console.error("Error removing share:", error);
    }
  };


  const startEditPortfolio = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio);
    setNewPortfolioName(portfolio.name);
    setNewPortfolioDescription(portfolio.description || '');
    setShowEditForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  // Mobile View: Render native dropdown
  if (mobile) {
    return (
      <div data-testid="portfolio-dropdown" className="portfolio-dropdown space-y-2">
        <div className="flex items-center justify-between px-3 py-2">
          <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Portfolio</h3>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-1 px-2 py-1 text-xs bg-accent-positive-light text-accent-positive-dark rounded hover:bg-accent-positive-medium transition-colors"
          >
            <Plus className="h-3 w-3" />
            <span>New</span>
          </button>
        </div>

        <select
          value={activePortfolio?.id || ''}
          onChange={async (e) => {
            const portfolio = portfolios.find(p => p.id === e.target.value);
            if (portfolio) {
              await switchPortfolio(portfolio);
              onMobileMenuClose?.();
              // ponytail: soft navigation only. Hard nav aborts in-flight
              // /api/batch and trips the error screen - see DashboardContext.
              router.push('/dashboard');
            }
          }}
          className="input appearance-none w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-lg bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-positive focus:border-transparent"
        >
          {portfolios.length === 0 ? (
            <option value="" disabled>No portfolios yet</option>
          ) : (
            portfolios.map((portfolio) => (
              <option key={portfolio.id} value={portfolio.id}>
                {portfolio.name}
              </option>
            ))
          )}
        </select>

        {/* Render Forms (Modal-like behavior for mobile) */}
        {(showCreateForm || showEditForm || showShareForm) && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
              {showCreateForm && userTier < SUBSCRIPTION_TIERS.ADVISOR && (
                <div className="p-4">
                  Upgrade to Advisor studio to have multiple portfolios
                  <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 btn btn-secondary">Cancel</button>
                </div>
              )}

              {showCreateForm && userTier >= SUBSCRIPTION_TIERS.ADVISOR && (
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Create New Portfolio</h3>
                  <form onSubmit={handleCreatePortfolio}>
                    <div className="mb-4">
                      <label htmlFor="field-portfolio-dropdown-1" className="block text-sm font-medium text-gray-700 mb-1">Portfolio Name *</label>
                      <input id="field-portfolio-dropdown-1" type="text" value={newPortfolioName} onChange={(e) => setNewPortfolioName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="field-portfolio-dropdown-2" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea id="field-portfolio-dropdown-2" value={newPortfolioDescription} onChange={(e) => setNewPortfolioDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={3} />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        disabled={isCreatingPortfolio}
                        className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isCreatingPortfolio ? (
                          <>
                            <span className="ml-2">Please wait</span>
                          </>
                        ) : (
                          'Create'
                        )}
                      </button>
                      <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 btn btn-secondary">Cancel</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    );
  }

  // Desktop View (Existing Dropdown Logic)
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Portfolio button */}
      <button
        data-testid="portfolio-dropdown"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center space-x-2 px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200 border-primary-500 text-gray-900"
      >
        <span>
          {activePortfolio ? (
            <>
              <span className="sm:hidden">
                {activePortfolio.name.length > 40 ?
                  `${activePortfolio.name.substring(0, 40)}...` :
                  activePortfolio.name}
              </span>
              <span className="hidden sm:inline">
                {activePortfolio.name.length > 10 ?
                  `${activePortfolio.name.substring(0, 10)}...` :
                  activePortfolio.name}
              </span>
            </>
          ) : 'No Portfolio'}
        </span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {/* Dropdown menu */}
      {dropdownOpen && (
        <div data-testid="portfolio-dropdown-menu" className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[100]">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Portfolios</h3>
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setShowCreateForm(true);
                }}
                className="cursor-pointer flex items-center space-x-1 px-2 py-1 text-xs bg-positive-100/20 text-positive-600 rounded hover:bg-authority-500/20 transition-colors"
              >
                <Plus className="h-3 w-3" />
                <span>New</span>
              </button>
            </div>
          </div>

          {/* Portfolio list */}
          <div className="max-h-64 overflow-y-auto">
            {portfolios.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No portfolios yet
              </div>
            ) : (
              portfolios
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((portfolio) => (
                  <div key={portfolio.id} className={`border-b border-gray-100 last:border-b-0 ${activePortfolio?.id === portfolio.id ? 'bg-positive-50/20' : ''}`}>
                    <div className={`flex items-center justify-between p-3 ${activePortfolio?.id === portfolio.id ? 'active-portfolio hover:bg-positive-100:bg-positive-900/30' : 'hover:bg-gray-50:bg-gray-700'}`}>
                      <button
                        onClick={async () => {
                          await switchPortfolio(portfolio);
                          // ponytail: soft navigation only. Hard nav aborts in-flight
                          // /api/batch and trips the error screen - see DashboardContext.
                          router.push('/dashboard');
                        }}
                        className="portfolio-item cursor-pointer flex items-center space-x-2 flex-1 text-left"
                      >
                        <div>
                          <div className="font-medium text-gray-900">{portfolio.name}</div>
                          <div className="items-center space-x-2 mt-1">
                            {portfolio.id === defaultPortfolioId && portfolio.isOwner && (
                              <div className="text-xs text-primary-600">Owner · Default</div>
                            )}
                            {portfolio.id === defaultPortfolioId && !portfolio.isOwner && portfolio.owner && (
                              <div className="text-xs text-primary-600">
                                Shared by {portfolio.owner.email.split('@')[0]}
                              </div>
                            )}
                            {portfolio.id !== defaultPortfolioId && portfolio.isOwner && (
                              <div className="text-xs text-primary-600">Owner</div>
                            )}
                            {!portfolio.isOwner && portfolio.userPermission && (
                              <div className={`text-xs flex items-center space-x-1 ${portfolio.userPermission === 'edit'
                                ? 'text-danger-600'
                                : 'text-warning-600'
                                }`}>
                                {portfolio.userPermission === 'edit' ? (
                                  <>
                                    <SquarePen className="h-3 w-3" />
                                    <span>Can edit</span>
                                  </>
                                ) : (
                                  <>
                                    <Eye className="h-3 w-3" />
                                    <span>View only</span>
                                  </>
                                )}
                              </div>
                            )}
                            {/* Show who shared this with you */}
                            {!portfolio.isOwner && portfolio.sharedWith && portfolio.sharedWith.length > 0 && (
                              <div className="text-xs text-gray-500">
                                Owner: {portfolio.sharedWith.map((u: { email: string }) => u.email).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>

                      {portfolio.isOwner && (
                        <div className="grid grid-cols-3 gap-1">
                          <button
                            onClick={() => {
                              setDropdownOpen(false);
                              startEditPortfolio(portfolio);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600:text-gray-300 hover:bg-gray-100:bg-gray-700 rounded"
                            title="Edit"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => {
                              setDropdownOpen(false);
                              setShowShareForm(true);
                            }}
                            className="p-1 text-gray-400 hover:text-positive-600:text-positive-400 hover:bg-positive-50:bg-positive-900/20 rounded"
                            title="Share"
                          >
                            <Share2 className="h-3 w-3" />
                          </button>
                          {portfolio.id === defaultPortfolioId || ownedPortfolioCount <= 1 ? (
                            <button
                              type="button"
                              disabled
                              aria-disabled="true"
                              className="p-1 text-gray-300 rounded cursor-not-allowed"
                              title={DEFAULT_PORTFOLIO_DELETE_TOOLTIP}
                              aria-label={DEFAULT_PORTFOLIO_DELETE_TOOLTIP}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeletePortfolio(portfolio.id)}
                              className="p-1 text-gray-400 hover:text-danger-600:text-danger-400 hover:bg-danger-50:bg-danger-900/20 rounded"
                              title="Delete"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-gray-200">
            <Link
              href="/settings"
              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100:bg-gray-700"
              onClick={() => setDropdownOpen(false)}
            >
              <BarChart3 className="h-4 w-4" />
              <span>Portfolio Settings</span>
            </Link>
            <Link
              href="/subscription"
              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100:bg-gray-700"
              onClick={() => setDropdownOpen(false)}
            >
              <Settings className="h-4 w-4" />
              <span>Subscription Settings</span>
            </Link>

          </div>
        </div>
      )}

      {/* Create portfolio form */}
      {showCreateForm && userTier < SUBSCRIPTION_TIERS.ADVISOR && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Upgrade to advisor studio to manage multiple portfolios</h3>

            <div className="flex space-x-3">
              <a href="mailto:support@delphina.money" className="flex-1 btn btn-primary">
                Upgrade
              </a>


              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewPortfolioName('');
                  setNewPortfolioDescription('');
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateForm && userTier >= SUBSCRIPTION_TIERS.ADVISOR && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Create New Portfolio</h3>
            <form onSubmit={handleCreatePortfolio}>
              <div className="mb-4">
                <label
                  htmlFor="portfolio-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Portfolio Name *
                </label>
                <input
                  id="portfolio-name"
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="My Portfolio"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="portfolio-description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description (optional)
                </label>
                <textarea
                  id="portfolio-description"
                  value={newPortfolioDescription}
                  onChange={(e) => setNewPortfolioDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="A description of your portfolio"
                  rows={3}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isCreatingPortfolio}
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isCreatingPortfolio ? (
                    <>
                      <LoadingSpinner size={16} />
                      <span className="ml-2">Please wait</span>
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewPortfolioName('');
                    setNewPortfolioDescription('');
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit portfolio form */}
      {showEditForm && editingPortfolio && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Edit Portfolio</h3>
            <form onSubmit={handleUpdatePortfolio}>
              <div className="mb-4">
                <label
                  htmlFor="edit-portfolio-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Portfolio Name *
                </label>
                <input
                  id="edit-portfolio-name"
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="edit-portfolio-description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description (optional)
                </label>
                <textarea
                  id="edit-portfolio-description"
                  value={newPortfolioDescription}
                  onChange={(e) => setNewPortfolioDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 btn btn-primary"
                >
                  Update Portfolio
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingPortfolio(null);
                    setNewPortfolioName('');
                    setNewPortfolioDescription('');
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share portfolio form */}
      {showShareForm && activePortfolio && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Share Portfolio: {activePortfolio.name}</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSharePortfolio(activePortfolio.id);
            }}>
              <div className="mb-4">
                <label
                  htmlFor="share-email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address *
                </label>
                <input
                  id="share-email"
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="field-portfolio-dropdown-3" className="block text-sm font-medium text-gray-700 mb-1">
                  Permission Level
                </label>
                <select
                  id="field-portfolio-dropdown-3" value={sharePermission}
                  onChange={(e) => setSharePermission(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="view">View Only</option>
                  <option value="edit">Can Edit</option>
                </select>
              </div>
              <div className="mb-4">
                <div className="text-xs text-gray-500">
                  <p><strong>View Only:</strong> Can see portfolio data but cannot make changes</p>
                  <p><strong>Can Edit:</strong> Can view and modify portfolio data</p>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 btn btn-primary"
                >
                  Share Portfolio
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowShareForm(false);
                    setShareEmail('');
                    setSharePermission('view');
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isCreatingPortfolio ? (
        <LoadingSpinner />
      ) : ("")}
    </div>
  );
}